import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { trackPurchase } from "../lib/analytics";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import {
  CheckCircle2,
  Download,
  Loader2,
  ShieldCheck,
  Mail,
  FileText,
  Archive,
  Package,
} from "lucide-react";

const fileIcon = (f) => {
  const ct = (f.content_type || "").toLowerCase();
  if (ct.includes("zip")) return Archive;
  return FileText;
};

const sizeLabel = (n) => (n ? `${(n / 1024 / 1024).toFixed(1)} MB` : "—");

export default function ThankYouPage() {
  const [params] = useSearchParams();
  const provider = params.get("provider");
  const sessionId = params.get("session_id");
  const tokenParam = params.get("token");

  const [state, setState] = useState({ loading: true, ok: false });
  const [manifest, setManifest] = useState(null);

  // Resolve the download token from URL (paypal) or Stripe session polling
  useEffect(() => {
    if (provider === "paypal" && tokenParam) {
      setState({ loading: false, ok: true, downloadToken: tokenParam });
      return;
    }
    if (provider === "stripe" && sessionId) {
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/checkout/stripe/status/${sessionId}`);
          if (data.payment_status === "paid" && data.download_token) {
            setState({ loading: false, ok: true, downloadToken: data.download_token });
            return;
          }
          if (data.status === "expired") {
            setState({ loading: false, ok: false, error: "Session expired." });
            return;
          }
        } catch (e) {
          if (attempts >= 8) {
            setState({ loading: false, ok: false, error: "Could not verify payment." });
            return;
          }
        }
        if (attempts < 8) setTimeout(poll, 2000);
        else setState({ loading: false, ok: false, error: "Payment verification timed out." });
      };
      poll();
      return;
    }
    setState({ loading: false, ok: false, error: "Missing order details." });
  }, [provider, sessionId, tokenParam]);

  // Fetch the file manifest once token is known
  useEffect(() => {
    if (!state.downloadToken) return;
    api
      .get(`/orders/${state.downloadToken}`)
      .then((r) => {
        setManifest(r.data);
        // Fire Google Ads conversion + GA4 purchase event once per order.
        // The manifest endpoint already enforces _is_paid(txn), so reaching
        // this point means the order is genuinely confirmed paid.
        trackPurchase({
          orderId: r.data.order_id,
          value: r.data.amount,
          currency: r.data.currency,
          productId: r.data.product_id,
          productName: r.data.product_name,
        });
      })
      .catch(() => setManifest(null));
  }, [state.downloadToken]);

  return (
    <div data-testid="thankyou-page" className="bg-[#F8FAFC] min-h-screen">
      <Header />

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-20">
        {state.loading && (
          <div className="bg-white border border-slate-200 p-10 sm:p-12 text-center">
            <Loader2 className="w-12 h-12 text-[#C5A059] animate-spin mx-auto mb-6" />
            <h1 className="font-display text-3xl text-[#0A192F] mb-3">Confirming your payment</h1>
            <p className="text-slate-600">Hold tight — this usually takes a few seconds.</p>
          </div>
        )}

        {!state.loading && state.ok && (
          <div data-testid="thankyou-success" className="bg-white border border-slate-200 p-6 sm:p-10">
            <div className="text-center mb-8 sm:mb-10">
              <CheckCircle2 className="w-14 h-14 text-[#10B981] mx-auto mb-5" />
              <span className="font-accent text-[#C5A059] text-xs">PAYMENT CONFIRMED</span>
              <h1 className="font-display text-3xl sm:text-5xl text-[#0A192F] mt-3 mb-4 leading-tight">
                Welcome to AIGL, founder.
              </h1>
              <p className="text-slate-600 max-w-md mx-auto text-sm sm:text-base">
                Your files are ready below. We've also emailed each download link so you can
                grab them from any device.
              </p>
            </div>

            {!manifest && (
              <div className="flex items-center justify-center py-10 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Preparing your files...
              </div>
            )}

            {manifest && (
              <div data-testid="files-manifest" className="space-y-6">
                {manifest.groups.map((g) => (
                  <div
                    key={g.product_id}
                    data-testid={`group-${g.product_id}`}
                    className="border border-slate-200"
                  >
                    {manifest.is_bundle && (
                      <div className="bg-[#0A192F] text-white px-4 sm:px-5 py-3 flex items-center gap-2">
                        <Package className="w-4 h-4 text-[#C5A059]" />
                        <div>
                          <div className="font-display text-sm sm:text-base">{g.product_name}</div>
                          {g.product_tagline && (
                            <div className="text-[10px] sm:text-xs text-white/60">
                              {g.product_tagline}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="divide-y divide-slate-200">
                      {g.files.map((f) => {
                        const Icon = fileIcon(f);
                        return (
                          <div
                            key={f.id}
                            data-testid={`file-row-${f.id}`}
                            className="flex items-center gap-3 px-4 sm:px-5 py-3.5"
                          >
                            <div className="w-9 h-9 bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <Icon className="w-4 h-4 text-[#0A192F]" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div
                                className="text-sm text-[#0A192F] truncate"
                                title={f.filename}
                              >
                                {f.filename}
                              </div>
                              <div className="text-[11px] text-slate-500 mt-0.5">
                                {f.is_placeholder ? "Auto-generated PDF" : sizeLabel(f.size_bytes)}
                                {(f.content_type || "").includes("zip") ? " · ZIP archive" : ""}
                              </div>
                            </div>
                            <a
                              href={f.url}
                              data-testid={`download-file-${f.id}`}
                              className="bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A192F] font-bold py-2 sm:py-2.5 px-3 sm:px-5 text-[11px] sm:text-xs transition inline-flex items-center gap-1.5 flex-shrink-0"
                            >
                              <Download className="w-3.5 h-3.5" />
                              DOWNLOAD
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {manifest.zip_url && (
                  <div className="text-center pt-2">
                    <a
                      href={manifest.zip_url}
                      data-testid="download-all-zip-btn"
                      className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#0A192F] underline underline-offset-4"
                    >
                      <Archive className="w-3.5 h-3.5" />
                      Or download all files as one ZIP
                    </a>
                  </div>
                )}
              </div>
            )}

            <div className="mt-10 pt-8 border-t border-slate-200 grid sm:grid-cols-2 gap-4 text-left">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#C5A059] mt-0.5" />
                <div>
                  <div className="font-display text-[#0A192F]">Check Your Inbox</div>
                  <p className="text-xs text-slate-500 mt-1">
                    We've emailed every download link. Check spam if you don't see it.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-[#C5A059] mt-0.5" />
                <div>
                  <div className="font-display text-[#0A192F]">Expert-Backed Plan</div>
                  <p className="text-xs text-slate-500 mt-1">
                    Meticulously researched frameworks trusted by hundreds of founders worldwide.
                  </p>
                </div>
              </div>
            </div>

            <Link
              to="/"
              data-testid="back-home-link"
              className="block text-center mt-10 text-sm text-slate-500 hover:text-[#0A192F]"
            >
              Back to home
            </Link>
          </div>
        )}

        {!state.loading && !state.ok && (
          <div data-testid="thankyou-error" className="bg-white border border-slate-200 p-12 text-center">
            <h1 className="font-display text-3xl text-[#0A192F] mb-3">
              We couldn't confirm your payment
            </h1>
            <p className="text-slate-600 mb-6">{state.error}</p>
            <Link
              to="/"
              className="inline-block bg-[#0A192F] text-white px-6 py-3 text-sm font-bold hover:bg-[#112240]"
            >
              Return Home
            </Link>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
